import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Could not send reset email.');
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-medium">Reset your password</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We'll email you a link to set a new one.
          </p>
        </div>

        {submitted ? (
          <div className="text-center space-y-4" data-testid="forgot-password-confirmation">
            <p className="text-sm">
              If an account exists for <b>{email}</b>, a reset link is on its way. The link expires in 30 minutes.
            </p>
            <p className="text-sm text-muted-foreground">
              Don't see it? Check your spam folder or wait a minute and try again.
            </p>
            <Link to="/auth" className="block text-sm text-primary font-medium hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                required placeholder="you@company.com"
                className="h-11 rounded-xl"
                data-testid="forgot-password-email-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 rounded-full font-medium"
              disabled={loading}
              data-testid="forgot-password-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
            </Button>
            <div className="text-center text-sm text-muted-foreground">
              <Link to="/auth" className="text-primary font-medium hover:underline">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
